import { Component, ErrorHandler, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { TypeaheadToimittajaDokumentti, TypeaheadToimittajaIlmanAvainta } from '@lemon/core-firebase/model/lasku'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'

import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'

import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'

import { NaytettavaKirjaus } from '../kirjanpito.component'

import { DebugService } from 'app/_angular/service/debug.service'
import { KirjaukseenLiitettyjenTiedostojenSivut } from 'app/_jaettu-lemonator/model/kirjanpito'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { combineLatest, Observable, of as observableOf } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'

export interface KirjausDiagnoseHelpDialogData {
  naytettavaKirjaus: NaytettavaKirjaus
}

@Component({
  templateUrl: './kirjaus.diagnose-help.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjausDiagnoseHelpDialog implements OnInit {
  liitetytTiedostotObservable: Observable<KirjaukseenLiitettyjenTiedostojenSivut>
  kirjausUriObservable: Observable<string>
  kirjausEncodedUriObservable: Observable<string>
  kirjaukseenLiitettyjenTiedostojenUriObservable: Observable<string>
  kirjaukseenLiitettyjenTiedostojenEncodedUriObservable: Observable<string>
  selvityspyynnonUritObservable: Observable<{ uri: string; encodedUri: string }[]>
  kirjauksenToimittajatObservable: Observable<string[]>

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KirjausDiagnoseHelpDialogData,
    private dialogRef: MatDialogRef<KirjausDiagnoseHelpDialog>,
    private errorHandler: ErrorHandler,
    private kopioija: KopioijaPalvelu,
    private _firebase: FirebaseLemonator,
    private asiakasService: AsiakasService,
    private ladataanService: LadataanService,
    private viitenumeroService: ViitenumeroService,
    private validationService: FormValidationService,
    private kirjanpitoUriService: KirjanpitoUriService,
    private debugService: DebugService,
    private _tositeUriService: TositeUriService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
  ) {
    this.kirjaukseenLiitettyjenTiedostojenUriObservable = asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot != null) {
          return this.kirjanpitoUriService.annaKirjaukseenLiitettyjenTiedostojenUri(avainTiedot.avain, data.naytettavaKirjaus.kirjaus.avain)
        }
        return ''
      }),
    )

    this.kirjaukseenLiitettyjenTiedostojenEncodedUriObservable = this.kirjaukseenLiitettyjenTiedostojenUriObservable.pipe(
      map(uri => {
        return this.debugService.createFirestoreLink(uri)
      }),
    )

    this.kirjausUriObservable = asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot != null) {
          return this.kirjanpitoUriService.annaKirjauksenUri(avainTiedot.avain, data.naytettavaKirjaus.kirjaus.avain)
        }
        return ''
      }),
    )

    this.kirjausEncodedUriObservable = this.kirjausUriObservable.pipe(
      map(uri => {
        return this.debugService.createFirestoreLink(uri)
      }),
    )

    this.selvityspyynnonUritObservable = asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot == null) {
          return []
        }
        return (data.naytettavaKirjaus.kirjaus.rivit ?? [])
          .filter(rivi => !!rivi.sp)
          .map(rivi => {
            const uri = this._tositeUriService.annaSelvityspyyntoUri(avainTiedot.avain, rivi.av)
            return {
              uri: uri,
              encodedUri: this.debugService.createFirestoreLinkLemonaid(uri),
            }
          })
      }),
    )

    this.liitetytTiedostotObservable = asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avainTiedot => {
        if (avainTiedot) {
          const kirjaukseenLiitettyjenTiedostojenUri = this.kirjanpitoUriService.annaKirjaukseenLiitettyjenTiedostojenUri(avainTiedot.avain, data.naytettavaKirjaus.kirjaus.avain)
          return this._firebase.firestoreDoc<KirjaukseenLiitettyjenTiedostojenSivut>(kirjaukseenLiitettyjenTiedostojenUri).listen()
        }
        return observableOf(null)
      }),
    )

    const toimittajaDokumenttiObservable = asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avainTiedot => {
        if (avainTiedot) {
          return this._firebaseLemonaid.firestoreDoc<TypeaheadToimittajaDokumentti>(this._laskuUriService.getTypeaheadToimittajaDokumentinUriNew(avainTiedot.avain)).listen()
        }
        return observableOf(null)
      }),
    )

    this.kirjauksenToimittajatObservable = combineLatest([this.liitetytTiedostotObservable, toimittajaDokumenttiObservable]).pipe(
      map(([liitetyt, toimittajaDokumentti]) =>
        this._annaKirjauksenToimittajat(data.naytettavaKirjaus.kirjaus.toimittajaAvaimet, liitetyt, new Map(Object.entries(toimittajaDokumentti ?? {}))),
      ),
    )
  }

  ngOnInit() {}

  private _annaKirjauksenToimittajat(
    toimittajaAvaimet: string[] | undefined,
    liitetyt: KirjaukseenLiitettyjenTiedostojenSivut | null,
    toimittajat: Map<string, TypeaheadToimittajaIlmanAvainta>,
  ) {
    const sivut = Object.values(liitetyt?.t ?? {})
    return (toimittajaAvaimet ?? []).map(avain => {
      const toimittaja = toimittajat.get(avain)
      const nimi = toimittaja ? `${toimittaja.numero} ${toimittaja.nimi}` : 'Ei rekisterissä'
      const kuittiAvaimet = Array.from(new Set(sivut.filter(sivu => sivu.a && sivu.kd?.toimittajaAvain === avain).map(sivu => sivu.a)))
      return `${nimi} (ta: ${avain} / ka: ${kuittiAvaimet.join(', ') || '-'})`
    })
  }
}
